import { create } from "zustand";
import { JOURNEY_STAGES } from "../../data/journeyStages";

export type AppMode = "recorrido" | "sede-envigado" | "red-nacional";
export type Quality = "baja" | "media" | "alta";
export type Screen = "portada" | "recorrido";
export type ViewMode = "3d" | "2d";

export interface JourneyState {
  screen: Screen;
  mode: AppMode;
  stageIndex: number;
  quality: Quality;
  guided: boolean;
  paused: boolean;
  viewMode: ViewMode;
  reducedMotion: boolean;
  fontScale: number;
  helpOpen: boolean;
  sourcesOpen: boolean;

  startJourney: (mode: AppMode) => void;
  nextStage: () => void;
  prevStage: () => void;
  goToStage: (index: number) => void;
  toggleGuided: () => void;
  startGuided: () => void;
  togglePause: () => void;
  resetJourney: () => void;
  setQuality: (quality: Quality) => void;
  setViewMode: (viewMode: ViewMode) => void;
  setReducedMotion: (value: boolean) => void;
  setFontScale: (value: number) => void;
  setHelpOpen: (open: boolean) => void;
  setSourcesOpen: (open: boolean) => void;
}

const clampIndex = (index: number): number =>
  Math.min(Math.max(index, 0), JOURNEY_STAGES.length - 1);

export const useJourneyStore = create<JourneyState>()((set) => ({
  screen: "portada",
  mode: "recorrido",
  stageIndex: 0,
  quality: "media",
  guided: true,
  paused: false,
  viewMode: "3d",
  reducedMotion: false,
  fontScale: 1,
  helpOpen: false,
  sourcesOpen: false,

  startJourney: (mode) => set({ screen: "recorrido", mode, stageIndex: 0, paused: false }),
  nextStage: () => set((s) => ({ stageIndex: clampIndex(s.stageIndex + 1) })),
  prevStage: () => set((s) => ({ stageIndex: clampIndex(s.stageIndex - 1) })),
  goToStage: (index) => set({ stageIndex: clampIndex(index) }),
  toggleGuided: () => set((s) => ({ guided: !s.guided })),
  // Inicia el recorrido guiado desde el principio: el botón debe producir
  // movimiento visible inmediato, no solo cambiar una bandera interna.
  startGuided: () => set({ guided: true, paused: false, stageIndex: 0 }),
  togglePause: () => set((s) => ({ paused: !s.paused })),
  resetJourney: () => set({ screen: "portada", stageIndex: 0, paused: false, guided: true }),
  setQuality: (quality) => set({ quality }),
  setViewMode: (viewMode) => set({ viewMode }),
  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setFontScale: (fontScale) => set({ fontScale: Math.min(Math.max(fontScale, 0.85), 1.5) }),
  setHelpOpen: (helpOpen) => set({ helpOpen }),
  setSourcesOpen: (sourcesOpen) => set({ sourcesOpen }),
}));

export const selectCurrentStage = (s: JourneyState) => JOURNEY_STAGES[s.stageIndex];
